import type { RootState, Screen } from "../types/domain";

export type AccessEntry = Screen | "home" | "auth" | "apply";

export const ACCESS_SCREEN_PATHS: Record<Screen, string> = {
  welcome: "/",
  signin: "/auth/sign-in",
  signup: "/auth/sign-up",
  "verify-email": "/auth/verify-email",
  mfa: "/auth/mfa",
  "forgot-password": "/auth/forgot-password",
  "reset-password": "/auth/reset-password",
  invite: "/auth/invite",
  workspaces: "/auth/workspaces",
  application: "/apply",
  status: "/apply/status",
  offer: "/apply/offer",
  checkout: "/apply/checkout",
  onboarding: "/auth/onboarding",
  complete: "/auth/complete",
};

const ACCOUNT_REQUIRED_SCREENS = new Set<Screen>([
  "workspaces",
  "application",
  "status",
  "offer",
  "checkout",
  "onboarding",
  "complete",
]);

export function visibleAccessScreen(state: RootState): Screen {
  return ACCOUNT_REQUIRED_SCREENS.has(state.screen) && !state.authenticated
    ? state.emailVerified
      ? "signin"
      : "signup"
    : state.screen;
}

const DIRECT_ONBOARDING_ROUTES = new Set([
  "teaching",
  "proctor",
  "organization",
  "institution",
  "academy-staff",
  "invited-member",
]);

/** Resolves the next saved-account destination without granting workspace access. */
export function accountJourneyScreen(state: RootState): Screen {
  if (state.onboarding.completedAt) return "complete";
  if (state.application.status === "enrolled") return "onboarding";
  if (state.application.status === "accepted") {
    return state.screen === "checkout" ? "checkout" : "onboarding";
  }
  if (state.application.status === "offered") return "offer";
  if (["submitted", "review", "interview"].includes(state.application.status)) return "status";
  if (DIRECT_ONBOARDING_ROUTES.has(state.application.route)) return "onboarding";
  return "application";
}

/** Resolves the public Academy Access entry without coupling the app to a router package. */
export function accessEntryFromPath(pathname = window.location.pathname): AccessEntry {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/") return "home";
  if (normalized === "/apply") return "apply";
  if (normalized === "/auth/create") return "signup";
  const match = (Object.entries(ACCESS_SCREEN_PATHS) as [Screen, string][]).find(
    ([, path]) => path === normalized,
  );
  if (match) return match[0];
  if (normalized.startsWith("/apply/")) return "apply";
  if (normalized === "/auth" || normalized.startsWith("/auth/")) return "auth";
  return "home";
}

export function stateForAccessEntry(state: RootState, entry = accessEntryFromPath()): RootState {
  if (entry === "apply") {
    if (!state.authenticated) return { ...state, screen: state.emailVerified ? "signin" : "signup" };
    return { ...state, screen: accountJourneyScreen(state) };
  }
  if (entry === "auth") return { ...state, screen: "signin" };
  if (entry === "home")
    return {
      ...state,
      screen: state.authenticated && state.onboarding.completedAt ? "complete" : "welcome",
    };
  const next = { ...state, screen: entry };
  return { ...next, screen: visibleAccessScreen(next) };
}

export function accessPathForScreen(screen: Screen) {
  return ACCESS_SCREEN_PATHS[screen];
}

/** Keeps refreshable public paths aligned with the currently visible access journey. */
export function updateAccessPath(screen: Screen, mode: "push" | "replace" = "replace") {
  if (typeof window === "undefined") return;
  const pathname = accessPathForScreen(screen);
  if (window.location.pathname === pathname) return;
  const url = `${pathname}${window.location.search}${window.location.hash}`;
  if (mode === "push") window.history.pushState(window.history.state, "", url);
  else window.history.replaceState(window.history.state, "", url);
}
